import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Award, Users, Target, CheckCircle2 } from 'lucide-react';
import ContactForm from '../components/ContactForm';
import ResponsiveCarousel from '../components/ResponsiveCarousel';
import { companyStats, whyChooseUsPoints } from '../data/mock';

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Us - Trusted Trademark Services | Trademark Frontline</title>
        <meta name="description" content="Learn about Trademark Frontline. 10,000+ trademarks registered with 98% approval rate. Your trusted partner in brand protection." />
      </Helmet>
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#4B0082]/5 to-[#FF7F7F]/10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <p className="text-[#FF7F7F] font-semibold text-lg mb-4">ABOUT US</p>
            <h1 className="text-5xl lg:text-6xl font-bold text-[#4B0082] mb-6 leading-tight">
              Your Trusted Partner in Brand Protection
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              At Trademark Frontline, we're dedicated to helping businesses protect their most valuable asset—their brand. With years of experience and thousands of successful registrations, we make trademark protection simple, affordable, and reliable.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="image-zoom rounded-2xl overflow-hidden shadow-xl hover-lift">
              <img
                src="https://images.unsplash.com/photo-1622675363311-3e1904dc1885"
                alt="Our Team"
                className="w-full h-[500px] object-cover"
              />
            </div>
            <div>
              <p className="text-[#FF7F7F] font-semibold text-sm mb-2">OUR STORY</p>
              <h2 className="text-4xl font-bold text-[#4B0082] mb-6">
                Simplifying Brand Protection for Businesses Nationwide
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Trademark Frontline was founded with a simple mission: to make trademark registration and brand protection accessible to businesses of all sizes. We saw too many entrepreneurs and small business owners struggling with complex legal processes, expensive attorney fees, and confusing paperwork.
                </p>
                <p>
                  Our team of experienced trademark specialists and legal professionals came together to create a better solution. We've streamlined the trademark registration process, eliminated unnecessary complications, and made professional brand protection affordable for everyone.
                </p>
                <p>
                  Today, we're proud to have helped thousands of businesses across all 50 states protect their brands. From startups launching their first product to established companies expanding their portfolio, we provide the expertise and support needed to secure your intellectual property rights.
                </p>
                <p>
                  Our commitment goes beyond just filing paperwork. We believe in building long-term relationships with our clients, providing ongoing support, monitoring services, and expert guidance whenever you need it. Your success is our success.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF7F7F] font-semibold text-sm mb-2">OUR VALUES</p>
            <h2 className="text-4xl font-bold text-[#4B0082] mb-4">
              What Drives Us Every Day
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Our core values guide everything we do, from how we serve our clients to how we operate as a team.
            </p>
          </div>

          <ResponsiveCarousel
            items={[
              { icon: <Award className="h-8 w-8 text-[#FF7F7F] icon-pulse" />, title: 'Excellence', description: 'We hold ourselves to the highest standards of quality and professionalism. Every application we file receives meticulous attention to detail and expert review.' },
              { icon: <Users className="h-8 w-8 text-[#FF7F7F] icon-bounce" />, title: 'Client-Focused', description: 'Your success is our priority. We listen to your needs, answer your questions promptly, and provide personalized service that makes you feel valued and supported.' },
              { icon: <Target className="h-8 w-8 text-[#FF7F7F] icon-spin" />, title: 'Integrity', description: 'We operate with complete transparency and honesty. No hidden fees, no false promises—just straightforward, ethical service you can trust.' }
            ]}
            renderItem={(value, index) => (
              <Card className="border-2 border-transparent hover:border-[#FF7F7F] transition-all hover:shadow-xl hover-lift animate-scale-in group" style={{ animationDelay: `${(index + 1) * 0.1}s` }}>
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-[#FF7F7F]/10 rounded-full flex items-center justify-center mb-6 mx-auto group-hover:bg-[#FF7F7F]/20 transition-colors">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-bold text-[#4B0082] mb-4 group-hover:text-[#FF7F7F] transition-colors">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </CardContent>
              </Card>
            )}
            gridCols="md:grid-cols-3"
            gap="gap-8"
          />
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-[#4B0082] to-[#3A0062] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Track Record</h2>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto">
              Numbers that demonstrate our commitment to excellence and client success.
            </p>
          </div>

          <ResponsiveCarousel
            items={companyStats}
            renderItem={(stat, index) => (
              <div className="text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/20 transition-all hover:transform hover:scale-105">
                  <div className="text-5xl font-bold text-[#FF7F7F] mb-3">{stat.number}</div>
                  <div className="text-xl font-semibold mb-3">{stat.label}</div>
                  <p className="text-gray-300 text-sm">{stat.description}</p>
                </div>
              </div>
            )}
            gridCols="md:grid-cols-3"
            gap="gap-8"
          />
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#FF7F7F] font-semibold text-sm mb-2">WHY CHOOSE US</p>
            <h2 className="text-4xl font-bold text-[#4B0082] mb-4">
              What Sets Trademark Frontline Apart
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              We're not just another trademark service. Here's what makes us different.
            </p>
          </div>

          <ResponsiveCarousel
            items={whyChooseUsPoints}
            renderItem={(point, index) => (
              <Card className="border-2 border-transparent hover:border-[#FF7F7F] transition-all hover:shadow-xl hover-lift animate-scale-in group" style={{ animationDelay: `${index * 0.15}s` }}>
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-[#4B0082] mb-4 group-hover:text-[#FF7F7F] transition-colors">{point.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{point.description}</p>
                </CardContent>
              </Card>
            )}
            gridCols="md:grid-cols-3"
            gap="gap-8"
          />
        </div>
      </section>

      {/* Our Commitment Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#FF7F7F] font-semibold text-sm mb-2">OUR COMMITMENT</p>
              <h2 className="text-4xl font-bold text-[#4B0082] mb-6">
                Your Brand Protection is Our Priority
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                When you choose Trademark Frontline, you're not just getting a service provider—you're gaining a dedicated partner in protecting your brand's future. We understand that your brand represents years of hard work, creativity, and investment.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-[#FF7F7F] mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-[#4B0082] mb-1">Personalized Service</h3>
                    <p className="text-gray-600">Every client receives individualized attention and customized solutions for their unique needs.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-[#FF7F7F] mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-[#4B0082] mb-1">Ongoing Support</h3>
                    <p className="text-gray-600">Our relationship doesn't end at filing. We're here for renewals, monitoring, and any questions that arise.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle2 className="h-6 w-6 text-[#FF7F7F] mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-[#4B0082] mb-1">Results-Driven</h3>
                    <p className="text-gray-600">Our 98% approval rate speaks for itself. We do everything possible to secure your trademark registration.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="image-zoom rounded-2xl overflow-hidden shadow-xl hover-lift">
              <img
                src="https://images.unsplash.com/photo-1709715357520-5e1047a2b691"
                alt="Team Collaboration"
                className="w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#FF7F7F] to-[#FF6B6B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-4xl font-bold mb-6">
                Let's Protect Your Brand Together
              </h2>
              <p className="text-xl mb-8 text-white/90 leading-relaxed">
                Ready to secure your brand's future? Join thousands of satisfied clients who have trusted Trademark Frontline with their trademark registration and brand protection needs. Get started today with our special $45 offer.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <CheckCircle2 className="h-6 w-6 mr-3" />
                  <span className="text-lg">Fast, Reliable Service</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle2 className="h-6 w-6 mr-3" />
                  <span className="text-lg">Expert Legal Team</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle2 className="h-6 w-6 mr-3" />
                  <span className="text-lg">98% Approval Rate</span>
                </div>
              </div>
            </div>

            <div>
              <ContactForm
                title="Start Your Brand Protection"
                ctaText="Get Started - $45"
                showMessage={false}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default About;